import axios from 'axios';
import { BASE_URL } from '../constants/constants';
import { tokenStorage } from '../utils/tokenStorage';

export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  status: number;
}

export interface ApiError {
  message: string;
  status: number;
  data?: any;
}

class BaseApi {
  public api: any;

  constructor() {
    this.initialize();
  }

  private initialize = (): void => {
    this.api = axios.create({
      baseURL: BASE_URL + 'api/',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.addInterceptors();
  };

  private addInterceptors(): void {
    // Request interceptor - add JWT token to headers
    this.api.interceptors.request.use(
      (req: any) => {
        // Add JWT token (ID token) if available
        const accessToken = tokenStorage.getAccessToken();
        if (accessToken && req.headers) {
          req.headers['Authorization'] = `Bearer ${accessToken}`;
        }
        return req;
      },
      (error: any) => Promise.reject(error)
    );

    this.api.interceptors.response.use(
      (res: any) => res,
      (err: any) => this.responseFailureInterceptor(err)
    );
  }

  private responseFailureInterceptor = (error: any): Promise<never> => {
    if (error.response) {
      const { response: { status } } = error;
      if (status === 401) {
        // Clear tokens on 401
        tokenStorage.clearTokens();
        // Emit event for auth context to handle logout and redirect
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
      return Promise.reject(error.response);
    }
    return Promise.reject(new Error('Connection error'));
  };

  public get = (
    url: string, 
    options?: any
  ): Promise<any> => this.api.get(url, options).then((response: any) => response.data);

  public post = (
    url: string, 
    data?: any, 
    options?: any
  ): Promise<any> => this.api.post(url, data, options).then((response: any) => response.data);

  public delete = (
    url: string, 
    options?: any
  ): Promise<any> => this.api.delete(url, options).then((response: any) => response.data);

  public put = (
    url: string, 
    data?: any, 
    options?: any
  ): Promise<any> => this.api.put(url, data, options).then((response: any) => response.data);
}

export default BaseApi;

// Create a default instance for direct usage
export const baseAPI = new BaseApi();